import React, { useEffect, useState } from 'react';
import { billingService } from '../../api/services/billing.service';
import { CreditAccountDTO, InvoicePreviewDTO, formatMoney } from '../../types/billing';
import { formatServerUtcAsIst, formatYmd } from './closeBooksRules';
import { formatMinutes, invoicePreviewParams, usageKeyLabel } from './billingReportRules';

interface Props {
  account: CreditAccountDTO;
  onClose: () => void;
}

const filterInput =
  'px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm';
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';

/**
 * "The invoice so far": exactly the draft that Invoice usage would create for this
 * account right now (or before an IST cut-off), with nothing written — no invoice, no
 * number taken. A prepaid account's usage was paid by its top-ups, so it is shown for
 * information with the server's note.
 */
const InvoicePreviewModal: React.FC<Props> = ({ account, onClose }) => {
  /** IST `datetime-local`; blank = now. */
  const [cutoff, setCutoff] = useState('');
  const [taxPercent, setTaxPercent] = useState('');
  const [preview, setPreview] = useState<InvoicePreviewDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const params = invoicePreviewParams({ organizationId: account.organizationId, beforeIst: cutoff, taxPercent });
    billingService
      .getInvoicePreview({ organizationId: account.organizationId, before: params.before, taxPercent: params.taxPercent })
      .then((r) => {
        if (!live) return;
        if (r.success && r.data) {
          setPreview(r.data);
          setError(null);
        } else {
          setPreview(null);
          setError(r.error || 'Failed to load the invoice so far');
        }
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [account.organizationId, cutoff, taxPercent]);

  const money = (v?: number | null) => formatMoney(v ?? 0, preview?.currency || account.currency || 'INR');
  const name = preview?.organizationName || account.organizationName || account.organizationId;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl max-h-[90vh] flex flex-col mx-4">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold dark:text-white">Invoice so far · {name}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              What invoicing usage would produce now. Nothing is created. Times in IST.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className={labelCls}>Usage before (IST)</label>
              <input
                type="datetime-local"
                aria-label="Usage before (IST)"
                value={cutoff}
                onChange={(e) => setCutoff(e.target.value)}
                className={filterInput}
              />
            </div>
            <div>
              <label className={labelCls}>GST %</label>
              <input
                type="number"
                min="0"
                step="0.01"
                aria-label="GST percent"
                placeholder="18"
                value={taxPercent}
                onChange={(e) => setTaxPercent(e.target.value)}
                className={`${filterInput} w-24`}
              />
            </div>
            <button onClick={onClose} className="self-start text-gray-400 hover:text-gray-600 text-2xl leading-none" aria-label="Close">
              &times;
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
          </div>
        ) : error || !preview ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : (
          <>
            {!preview.invoiceable && (
              <div className="rounded-lg border p-3 mb-3 text-sm bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300">
                {preview.note || 'This account is not invoiced for usage.'}
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3 text-sm">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Billing period</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {preview.billingPeriodStart ? `${formatYmd(preview.billingPeriodStart)} – ${formatYmd(preview.billingPeriodEnd)}` : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Usage from / to</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {preview.firstOccurredAt
                    ? `${formatServerUtcAsIst(preview.firstOccurredAt)} – ${formatServerUtcAsIst(preview.lastOccurredAt)}`
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Cut-off</p>
                <p className="font-medium text-gray-900 dark:text-white">before {formatServerUtcAsIst(preview.before)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Charges · minutes</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {preview.charges} · {formatMinutes(preview.billedMinutes)}
                </p>
              </div>
            </div>

            <div className="overflow-auto flex-1 border border-gray-200 dark:border-gray-700 rounded">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    <th className={`${th} text-left`}>Business</th>
                    <th className={`${th} text-left`}>Service</th>
                    <th className={`${th} text-left`}>Carrier</th>
                    <th className={`${th} text-left`}>Description</th>
                    <th className={`${th} text-right`}>Qty</th>
                    <th className={`${th} text-right`}>Unit price</th>
                    <th className={`${th} text-right`}>Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {preview.lines.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">No uninvoiced usage</td></tr>
                  ) : (
                    preview.lines.map((l, i) => (
                      <tr key={`${l.businessId}-${l.serviceType}-${l.carrier}-${i}`} title={`${l.charges} charge(s)`}>
                        <td className={td}>{l.business || l.businessId || '-'}</td>
                        <td className={td}>{usageKeyLabel(l.serviceType)}</td>
                        <td className={td}>{l.carrier || '-'}</td>
                        <td className={`${td} whitespace-normal`}>{l.description}</td>
                        <td className={`${td} text-right`}>{formatMinutes(l.quantity)} {l.unit === 'x' ? '×' : l.unit}</td>
                        <td className={`${td} text-right`}>{money(l.unitPrice)}</td>
                        <td className={`${td} text-right`}>{money(l.amount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <td colSpan={6} className={`${td} text-right`}>Subtotal</td>
                    <td className={`${td} text-right`}>{money(preview.subtotal)}</td>
                  </tr>
                  <tr>
                    <td colSpan={6} className={`${td} text-right`}>GST {preview.taxRate}%</td>
                    <td className={`${td} text-right`}>{money(preview.taxAmount)}</td>
                  </tr>
                  <tr>
                    <td colSpan={6} className={`${td} text-right font-semibold`}>Total</td>
                    <td className={`${td} text-right font-semibold`}>{money(preview.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoicePreviewModal;
